/**
 * Screen Wake Lock hook — holds `navigator.wakeLock` while `active` so the
 * device's screen doesn't sleep. Used by the Workspace (behind the per-device
 * "Keep screen awake" opt-in) while Synthase is working: a locked/backgrounded
 * page is frozen by the OS, which makes the live preview unreachable for the
 * preview tools, so keeping the screen on is what keeps preview interaction
 * available on phones.
 *
 * The browser releases a wake lock automatically whenever the page is hidden;
 * this hook re-acquires it when the page becomes visible again while still
 * `active`. Everything is best-effort — an unsupported browser or a rejected
 * request (e.g. low-battery mode) simply means no lock, never an error.
 *
 * @module
 */

import { useEffect } from 'react'

/** Minimal structural type for the Wake Lock API (lib.dom has it, but keep the
 *  usage explicit and null-safe). */
interface WakeLockSentinelLike {
  release: () => Promise<void>
  addEventListener?: (type: 'release', listener: () => void) => void
}

/**
 * Holds a screen wake lock while `active` is true (and the API is available),
 * re-acquiring on visibility regains and releasing on deactivate/unmount.
 *
 * @param active - Whether the screen should currently be kept awake.
 */
export function useScreenWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return

    let sentinel: WakeLockSentinelLike | null = null
    let disposed = false

    const acquire = async (): Promise<void> => {
      if (disposed || document.visibilityState !== 'visible') return
      try {
        const wl = (
          navigator as unknown as {
            wakeLock: { request: (type: 'screen') => Promise<WakeLockSentinelLike> }
          }
        ).wakeLock
        const s = await wl.request('screen')
        if (disposed) {
          void s.release().catch(() => undefined)
          return
        }
        sentinel = s
      } catch (_error) {
        // Request can be rejected (low-battery mode, permissions policy) —
        // keeping the screen awake is a best-effort nicety, never an error.
      }
    }

    // The UA auto-releases the lock when the page hides; re-acquire on return.
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void acquire()
    }

    void acquire()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisibility)
      void sentinel?.release().catch(() => undefined)
      sentinel = null
    }
  }, [active])
}
